
import { formatCurrency_legacy } from '../../../utils/currency';
import HHighchart from '../../../components/wrappers/h-highchart';

export default {
  name: 'ArticleGraph',

  components: {
    HHighchart,
  },

  props: {
    containerClass: {
      type: String,
      default: '',
    },
    dataFormat: {
      type: String,
      default: 'legacy',
    },
    jiyuData: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      updateArgs: [
        'options.title',
        'options.series',
        'options.yAxis',
        'options.xAxis',
      ],
    };
  },

  computed: {
    chartType() {
      return this.jiyuData?.type?.toLowerCase();
    },

    jiyuChartTitle() {
      return this.jiyuData?.title;
    },

    jiyuSeries() {
      return this.chartType === 'line'
        ? this.lineJiyuSeries
        : this.columnJiyuSeries;
    },

    lineJiyuSeries() {
      return this.jiyuData.series.map((serie) => {
        return {
          name: serie.serieName,
          data: this.formatData(serie.data),
          dashStyle:
            serie?.style?.toLowerCase() === 'dotted' ? 'dash' : 'solid',
        };
      });
    },

    columnJiyuSeries() {
      return this.jiyuData?.series?.map((serie) => {
        return {
          name: serie.serieName,
          data: this.formatData(serie.data),
          color: serie.color,
        };
      });
    },

    domainLegendTitle() {
      return this.jiyuData?.domains[0]?.domain_name;
    },

    domainLineData() {
      return this.jiyuData?.domains[0]?.data;
    },

    useDomainLineData() {
      return (
        this.jiyuData?.domains[0].data?.length ==
        this.jiyuData?.series[0].data.length
      );
    },

    chartOptions() {
      return this.chartType === 'line' || this.chartType === 'area'
        ? this.lineChartOptions
        : this.columnChartOptions;
    },
    areaChartColorOptions() {
      return this.chartType === 'area'
        ? {
            color: this.$colors.primary.base,
            type: 'area',
            fillColor: {
              linearGradient: {
                x1: 0,
                y1: 0,
                x2: 0,
                y2: 1,
              },
              stops: [
                [0, 'rgba(27, 131, 145, 1)'],
                [1, 'rgba(255, 255, 255, 0)'],
              ],
            },
            zones: [
              {
                value: 0,
                color: '#DB695D',
                fillColor: {
                  linearGradient: {
                    x1: 0,
                    y1: 0,
                    x2: 0,
                    y2: 1,
                  },
                  stops: [
                    [0.1, 'rgba(255, 255, 255, 0)'],
                    [0.7, 'rgba(255, 255, 255, 0)'],
                    [1, 'rgba(219, 105, 93, 1)'],
                  ],
                },
              },
              {
                value: 0,
                color: this.$colors.primary.base,
              },
            ],
          }
        : {};
    },
    updatedJiyuSeries() {
      const first = { ...this.jiyuSeries[0], ...this.areaChartColorOptions };
      return this.chartType === 'area'
        ? [first, ...this.jiyuSeries.slice(1)]
        : this.jiyuSeries;
    },
    lineChartOptions() {
      const vueContext = this;

      return {
        colors: [
          this.$colors.secondary.base,
          this.$colors.peach.base,
          this.$colors.green.darken1,
          this.$colors.accent.base,
          this.$colors.error.darken1,
        ],

        chart: {
          type: 'line',
          style: {
            fontFamily: 'TTNorms',
            fontSize: 14,
            color: this.$colors.secondary.lighten2,
          },
        },
        responsive: {
          rules: [
            {
              condition: {
                maxWidth: 500,
              },
              chartOptions: {
                legend: {
                  align: 'center',
                  verticalAlign: 'bottom',
                  layout: 'horizontal',
                },
                subtitle: {
                  text: null,
                },
                credits: {
                  enabled: false,
                },
              },
            },
          ],
        },
        tooltip: {
          shadow: false,
          enabled: true,
          useHTML: true,
          formatter() {
            return `
                <div class="tooltip tooltip--dark">
                  <div class="tooltip__header">
                     ${vueContext.$t('year', { n: this.x })}
                  </div>
                  <div class="tooltip__body">
                    ${vueContext.formatDataTooltip(this.y)}
                  </div>
                </div>
              `;
          },
        },
        title: {
          text: undefined,
        },
        xAxis: {
          categories:
            this.useDomainLineData && this.chartType !== 'area'
              ? this.domainLineData
              : undefined,
          title: {
            text: this.domainLegendTitle,
            style: {
              color: this.$colors.grey.base,
              fontSize: '14px',
              fontWeight: 500,
            },
          },
          labels: {
            style: {
              color: this.$colors.secondary.lighten2,
              fontSize: '14px',
            },
          },
        },
        yAxis: {
          lineColor: this.$colors.grey.lighten1,
          lineWidth: 1,
          labels: {
            style: {
              color: this.$colors.secondary.lighten2,
              fontSize: '14px',
            },
            formatter() {
              if (vueContext.dataFormat == 'legacy') {
                return vueContext.formatWithUnits(this.value);
              } else if (vueContext.dataFormat == 'percentage') {
                return this.value + '%';
              } else {
                console.log('fall back data format');
                // fallback in case
                return vueContext.formatWithUnits(this.value);
              }
            },
          },
          tickColor: this.$colors.grey.lighten1,
          minorTickColor: this.$colors.grey.lighten1,
          title: {
            text: undefined,
          },
        },
        credits: {
          enabled: false,
        },
        legend: {
          enabled: true,
          itemStyle: {
            color: '#00324F',
            fontWeight: 400,
            cursor: 'default',
            fontSize: '14px',
            '-webkit-font-smoothing': 'antialiased',
            '-moz-osx-font-smoothing': 'grayscale',
          },
        },
        plotOptions: {
          series: {
            marker: {
              enabled: this.chartType === 'area',
            },
          },
          line: {
            color: this.chartType === 'area' ? this.$colors.primary.base : '',
            allowOverlap: true,
          },
        },
        series: this.updatedJiyuSeries,
      };
    },

    columnChartOptions() {
      const vueContext = this;

      const colors = this.jiyuSeries
        .map((serie) => serie.color)
        .filter(Boolean);

      return {
        chart: {
          type: 'column',
          style: {
            fontFamily: 'TTNorms',
            fontSize: 12,
          },
        },
        colors: colors.length
          ? [...colors, ...this.columnGraphsColors]
          : this.columnGraphsColors,
        options: {
          scales: {
            x: {
              stacked: true,
            },
            y: {
              stacked: true,
            },
          },
        },
        responsive: {
          rules: [
            {
              condition: {
                maxWidth: 500,
              },
              chartOptions: {
                legend: {
                  align: 'center',
                  verticalAlign: 'bottom',
                  layout: 'horizontal',
                },
                subtitle: {
                  text: null,
                },
                credits: {
                  enabled: false,
                },
              },
            },
          ],
        },
        tooltip: {
          shadow: false,
          enabled: true,
          useHTML: true,
          padding: 10,
          formatter() {
            const label = vueContext.jiyuSeries?.filter((serie) => {
              return serie.data[this.point.index] === this.y;
            })[0].name;

            return `
                <div class="tooltip tooltip--dark">
                  <div class="tooltip__header">
                    ${label}
                  </div>
                  <div class="tooltip__body">
                    ${vueContext.formatDataTooltip(this.y)}
                  </div>
                </div>
              `;
          },
        },
        title: {
          text: vueContext.jiyuChartTitle,
        },
        xAxis: {
          startOnTick: false,
          lineColor: this.$colors.grey.lighten1,
          lineWidth: 1.5,
          tickColor: this.$colors.grey.lighten1,
          minorTickColor: this.$colors.grey.lighten1,
          categories: this.jiyuData?.domains[0].data,
          title: {
            text: undefined,
          },
        },
        yAxis: {
          reversedStacks: false,
          offset: -6,
          lineColor: this.$colors.grey.lighten1,
          lineWidth: 1.5,
          tickColor: this.$colors.grey.lighten1,
          minorTickColor: this.$colors.grey.lighten1,
          stackLabels: {
            enabled: false,
          },
          title: {
            text: undefined,
          },
        },
        credits: {
          enabled: false,
        },
        legend: {
          enabled: true,
        },
        plotOptions: {
          column: {
            stacking:
              this.jiyuData?.stackType == 'default' ? undefined : 'normal',
            dataLabels: {
              enabled: false,
            },
          },
        },
        series: this.jiyuSeries,
      };
    },

    columnGraphsColors() {
      return [
        this.$colors.green.darken1,
        this.$colors.green.base,
        this.$colors.green.lighten1,
        this.$colors.primary.base,
        this.$colors.peach.darken1,
        this.$colors.peach.base,
        this.$colors.peach.lighten2,
      ];
    },
  },

  watch: {
    axisY() {
      if (this.chartType === 'line') {
        this.chartOptions.series = this.jiyuSeries;
      }
    },
  },

  methods: {
    formatDataTooltip(point) {
      if (this.dataFormat == 'legacy') {
        return formatCurrency_legacy(parseInt(point.toFixed(0), 10), ' €');
      } else if (this.dataFormat == 'percentage') {
        return (Math.round(point * 100) / 100).toFixed(2) + '%';
      } else {
        console.log('fall back data format');
        // fallback in case
        return formatCurrency_legacy(parseInt(point.toFixed(0), 10), ' €');
      }
    },
    formatData(data) {
      let fdata = null;
      if (this.dataFormat == 'legacy') {
        fdata = data.map((value) => {
          return parseInt(value.toFixed(0), 10);
        });
      } else if (this.dataFormat == 'percentage') {
        fdata = data.map((value) => {
          return parseFloat((Math.round(value * 100) / 100).toFixed(2));
        });
      } else {
        console.log('fall back data format');
        // fallback in case
        fdata = data.map((value) => {
          return parseInt(value.toFixed(0), 10);
        });
      }
      return fdata;
    },
    formatWithUnits(value) {
      const M = 1000000;
      const K = 1000;
      const negativeK = -1000;
      const negativeM = -1000000;

      const suffix = this.$i18n.locale === 'de' ? 'T €' : 'K €';

      if (value >= M || value <= negativeM) {
        return formatCurrency_legacy(value / M) + 'M €';
      }
      if (value >= K || value <= negativeK) {
        return formatCurrency_legacy(value / K) + suffix;
      }

      return formatCurrency_legacy(value, ' €');
    },
  },
};
